import { CheckCircleIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Collapse,
  Flex,
  Link,
  Text,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import { Badge, Tooltip } from "components";
import { DateTime } from "luxon";
import { ReactNode } from "react";
import { SectionLabel } from "../SectionLabel";
import { QuantumDemandContact } from "./QuantumDemandContactCard.types";
interface ContactInfoRowProps {
  label: string;
  children: ReactNode;
}

const ContactInfoRow = ({ label, children }: ContactInfoRowProps) => {
  const mutedColor = useColorModeValue("gray.500", "gray.400");

  return (
    <Flex gap={4}>
      <Text color={mutedColor} minW="60px">
        {label}
      </Text>
      <Text>{children}</Text>
    </Flex>
  );
};

type EngagementRow = {
  type: string;
  colorScheme: string;
  label: string;
  date?: string;
};

interface QuantumDemandContactCardProps {
  contact: QuantumDemandContact;
}

export const QuantumDemandContactCard = ({
  contact,
}: QuantumDemandContactCardProps) => {
  const { isOpen, onToggle } = useDisclosure();

  const borderColor = useColorModeValue("gray.100", "gray.600");
  const dividerColor = useColorModeValue("gray.100", "gray.600");
  const background = useColorModeValue("white", "gray.700");
  const topicBackground = useColorModeValue("gray.50", "gray.600");
  const mutedColor = useColorModeValue("gray.500", "gray.400");
  const badgeVariant = useColorModeValue("subtle", "solid");

  const {
    firstName,
    lastName,
    jobTitle,
    hasOptedIn,
    email,
    telephone,
    linkedInProfileUrl,
    contactEngagementSummary,
    engagement,
    topicsResearched,
  } = contact;

  const engagementRows: EngagementRow[] = [
    ...engagement.pageVisits.map((visit) => ({
      type: "Page Visit",
      colorScheme: "green",
      label: visit.url,
      date: visit.date,
    })),
    ...engagement.emailsReceived.map((emailReceived) => ({
      type: "Email Received",
      colorScheme: "cyan",
      label: emailReceived.subject ?? "",
      date: emailReceived.date,
    })),
    ...engagement.emailOpens.map((emailOpen) => ({
      type: "Email Opened",
      colorScheme: "cyan",
      label: emailOpen.subject ?? "",
      date: emailOpen.date,
    })),
    ...engagement.emailClicks.map((emailClick) => ({
      type: "Email Clicked",
      colorScheme: "cyan",
      label: emailClick.subject ?? "",
      date: emailClick.date,
    })),
    ...engagement.adClicks.map((adClick) => ({
      type: "Ad Click",
      colorScheme: "yellow",
      label: "",
      date: adClick.date,
    })),
    ...engagement.contentDownloads.map((download) => ({
      type: "Content Downloaded",
      colorScheme: "yellow",
      label: download.asset,
      date: download.date,
    })),
  ];

  return (
    <Box
      border="1px solid"
      borderColor={borderColor}
      borderRadius="lg"
      background={background}
      px="18px"
      py={4}
      w="100%"
    >
      <Flex alignItems="center" gap={2}>
        <Text fontSize="16px" fontWeight="bold" lineHeight="short">
          {firstName} {lastName}
        </Text>
        {hasOptedIn && (
          <Tooltip label="Opted-in contact" shouldWrapChildren>
            <CheckCircleIcon boxSize={4} color="teal.500" />
          </Tooltip>
        )}
      </Flex>

      <Text fontSize="14px" color={mutedColor} mt={0.5}>
        {jobTitle}
      </Text>

      <Button
        variant="link"
        colorScheme="blue"
        fontWeight="normal"
        fontSize="14px"
        mt={1.5}
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        {isOpen ? "Hide contact information" : "Show contact information"}
      </Button>

      <Collapse in={isOpen} animateOpacity>
        <Flex flexDirection="column" gap={1} mt={2} fontSize="14px">
          <ContactInfoRow label="Email">
            <Link href={`mailto:${email}`}>{email}</Link>
          </ContactInfoRow>
          {telephone && (
            <ContactInfoRow label="Phone">
              <Link href={`tel:${telephone}`}>{telephone}</Link>
            </ContactInfoRow>
          )}
          {linkedInProfileUrl && (
            <Link
              href={
                linkedInProfileUrl.includes("http")
                  ? linkedInProfileUrl
                  : `https://${linkedInProfileUrl}`
              }
              isExternal
              color="blue.500"
            >
              View LinkedIn Profile →
            </Link>
          )}
        </Flex>
      </Collapse>

      {topicsResearched.length > 0 && (
        <Box mt={3}>
          <SectionLabel>Persona-Level Research</SectionLabel>
          <Flex flexWrap="wrap" gap={1.5}>
            {topicsResearched.map((topic) => (
              <Box
                key={topic}
                background={topicBackground}
                borderRadius="md"
                fontSize="12px"
                px={2}
                py={1}
              >
                {topic}
              </Box>
            ))}
          </Flex>
        </Box>
      )}

      {engagementRows.length > 0 && (
        <Box mt={3}>
          <SectionLabel>Contact-Level Engagements</SectionLabel>
          {engagementRows.map((row, index) => (
            <Flex
              key={`${row.type}-${row.label}-${index}`}
              alignItems="center"
              gap={2.5}
              py={1.5}
              borderBottom={
                index === engagementRows.length - 1 ? undefined : "1px dashed"
              }
              borderColor={dividerColor}
            >
              <Badge
                variant={badgeVariant}
                colorScheme={row.colorScheme}
                borderRadius="md"
                textTransform="none"
                fontSize="11px"
                fontWeight="semibold"
                minW="110px"
                textAlign="center"
                px={2}
                py={0.5}
              >
                {row.type}
              </Badge>
              <Tooltip label={row.label} isDisabled={!row.label}>
                <Text flex={1} fontSize="12px" noOfLines={1}>
                  {row.label}
                </Text>
              </Tooltip>
              {row.date && (
                <Text fontSize="12px" color={mutedColor} whiteSpace="nowrap">
                  {DateTime.fromISO(row.date).toFormat("LLL d")}
                </Text>
              )}
            </Flex>
          ))}
        </Box>
      )}

      {contactEngagementSummary && (
        <Box mt={3}>
          <SectionLabel>Next Best Action</SectionLabel>
          <Text fontSize="14px">{contactEngagementSummary}</Text>
        </Box>
      )}
    </Box>
  );
};
